import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

// Удаляем константы и оставляем только функции
const getColorStyles = (color = 'primary') => {
  const colors = {
    primary: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f50057',
      light: '#ff4081',
      dark: '#c51162',
      contrastText: '#ffffff',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      contrastText: '#ffffff',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#000000',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2', 
      contrastText: '#ffffff',
    },
    default: {
      main: '#e0e0e0',
      light: '#f5f5f5',
      dark: '#9e9e9e',
      contrastText: '#000000',
    },
  };

  return colors[color] || colors.primary;
};

const getSizeStyles = (size = 'medium') => {
  const sizes = {
    small: {
      padding: '4px 10px',
      fontSize: '0.8125rem',
      borderRadius: '4px',
    },
    medium: {
      padding: '6px 16px',
      fontSize: '0.875rem',
      borderRadius: '4px',
    },
    large: {
      padding: '8px 22px',
      fontSize: '0.9375rem',
      borderRadius: '6px',
    },
  };

  return sizes[size] || sizes.medium;
};

// Основной контейнер кнопки
const ButtonBase = styled(motion.button)`
  font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-weight: 500;
  line-height: 1.75;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  background-color: transparent;
  outline: 0;
  border: 0;
  margin: 0;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  flex: 0 0 auto;
  border-radius: 4px;
  padding: 8px 16px;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, 
    box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, 
    border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, 
    color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  
  &:disabled {
    cursor: default;
    pointer-events: none;
    opacity: 0.6;
  }

  ${({ size }) => {
    const sizeProps = getSizeStyles(size);
    return css`
      padding: ${sizeProps.padding};
      font-size: ${sizeProps.fontSize};
      border-radius: ${sizeProps.borderRadius};
      
      @media (max-width: 576px) {
        padding: ${size === 'large' ? '8px 16px' : sizeProps.padding};
        font-size: ${size === 'large' ? '0.875rem' : sizeProps.fontSize};
      }
    `;
  }}

  ${({ startIcon }) => startIcon && css`
    & > .startIcon {
      display: inherit;
      margin-right: 8px;
      margin-left: -4px;
      
      @media (max-width: 576px) {
        margin-right: 6px;
        margin-left: -2px;
      }
    }
  `}

  ${({ endIcon }) => endIcon && css`
    & > .endIcon {
      display: inherit;
      margin-right: -4px;
      margin-left: 8px;
      
      @media (max-width: 576px) {
        margin-right: -2px;
        margin-left: 6px;
      }
    }
  `}

  ${({ fullWidth }) => fullWidth && css`
    width: 100%;
  `}
  
  ${({ disableElevation }) => disableElevation && css`
    box-shadow: none;
    &:hover {
      box-shadow: none;
    }
  `}
  
  /* Добавим улучшенную тактильную обратную связь для мобильных устройств */
  @media (max-width: 768px) {
    &:active {
      transform: scale(0.98);
    }
  }
`;

const ContainedButton = styled(ButtonBase)`
  ${({ color }) => {
    const colorProps = getColorStyles(color);
    return css`
      color: ${colorProps.contrastText};
      background-color: ${colorProps.main};
      box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2),
                  0px 2px 2px 0px rgba(0,0,0,0.14),
                  0px 1px 5px 0px rgba(0,0,0,0.12);
      
      &:hover {
        background-color: ${colorProps.dark};
        box-shadow: 0px 2px 4px -1px rgba(0,0,0,0.2),
                    0px 4px 5px 0px rgba(0,0,0,0.14),
                    0px 1px 10px 0px rgba(0,0,0,0.12);
                    
        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: inherit;
          animation: pulse 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite;
        }
        
        @keyframes pulse {
          0% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(${parseInt(colorProps.main.slice(1, 3), 16)}, 
                                       ${parseInt(colorProps.main.slice(3, 5), 16)}, 
                                       ${parseInt(colorProps.main.slice(5, 7), 16)}, 0.4);
          }
          70% {
            transform: scale(1);
            box-shadow: 0 0 0 10px rgba(${parseInt(colorProps.main.slice(1, 3), 16)}, 
                                         ${parseInt(colorProps.main.slice(3, 5), 16)}, 
                                         ${parseInt(colorProps.main.slice(5, 7), 16)}, 0);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(${parseInt(colorProps.main.slice(1, 3), 16)}, 
                                       ${parseInt(colorProps.main.slice(3, 5), 16)}, 
                                       ${parseInt(colorProps.main.slice(5, 7), 16)}, 0);
          }
        }
      }

      &:active {
        box-shadow: 0px 5px 5px -3px rgba(0,0,0,0.2),
                    0px 8px 10px 1px rgba(0,0,0,0.14),
                    0px 3px 14px 2px rgba(0,0,0,0.12);
      }
    `;
  }}
`;

const OutlinedButton = styled(ButtonBase)`
  ${({ color }) => {
    const colorProps = getColorStyles(color);
    return css`
      color: ${colorProps.main};
      border: 1px solid ${colorProps.main};
      
      &:hover {
        background-color: rgba(${parseInt(colorProps.main.slice(1, 3), 16)}, 
                                ${parseInt(colorProps.main.slice(3, 5), 16)}, 
                                ${parseInt(colorProps.main.slice(5, 7), 16)}, 0.04);
        border: 1px solid ${colorProps.dark};
      }
    `;
  }}
`;

const TextButton = styled(ButtonBase)`
  ${({ color }) => {
    const colorProps = getColorStyles(color);
    return css`
      color: ${colorProps.main};
      
      &:hover {
        background-color: rgba(${parseInt(colorProps.main.slice(1, 3), 16)}, 
                                ${parseInt(colorProps.main.slice(3, 5), 16)}, 
                                ${parseInt(colorProps.main.slice(5, 7), 16)}, 0.04);
      }
    `;
  }}
`;

// Компонент кнопки
const Button = forwardRef(
  ({ 
    children, 
    variant = 'contained', 
    color = 'primary', 
    size = 'medium',
    disableElevation = false,
    fullWidth = false,
    startIcon = null,
    endIcon = null,
    ...props 
  }, ref) => {
    const ButtonComponent = 
      variant === 'contained' ? ContainedButton :
      variant === 'outlined' ? OutlinedButton :
      TextButton;

    return (
      <ButtonComponent
        ref={ref}
        color={color}
        size={size}
        disableElevation={disableElevation}
        fullWidth={fullWidth}
        startIcon={!!startIcon}
        endIcon={!!endIcon}
        {...props}
      >
        {startIcon && <span className="startIcon">{startIcon}</span>}
        {children}
        {endIcon && <span className="endIcon">{endIcon}</span>}
      </ButtonComponent>
    );
  }
);

Button.displayName = 'Button';

// Кнопка-иконка (IconButton)
const IconButtonBase = styled(ButtonBase)`
  padding: ${(props) => 
    props.size === 'small' 
      ? '6px' 
      : props.size === 'large' 
        ? '14px' 
        : '10px'
  };
  min-width: auto;
  width: ${(props) => 
    props.size === 'small' 
      ? '32px' 
      : props.size === 'large' 
        ? '48px' 
        : '40px'
  };
  height: ${(props) => 
    props.size === 'small' 
      ? '32px' 
      : props.size === 'large' 
        ? '48px' 
        : '40px'
  };
  border-radius: 50%;
  color: ${(props) => {
    if (props.color === 'primary') return 'var(--color-primary, #2196f3)';
    if (props.color === 'secondary') return 'var(--color-secondary, #9c27b0)';
    if (props.color === 'error') return 'var(--color-error, #d32f2f)';
    if (props.color === 'warning') return 'var(--color-warning, #ed6c02)';
    if (props.color === 'info') return 'var(--color-info, #0288d1)';
    if (props.color === 'success') return 'var(--color-success, #2e7d32)';
    return 'var(--color-text, #333333)';
  }};
  
  &:hover {
    background-color: ${(props) => {
      if (props.color === 'primary') return 'rgba(33, 150, 243, 0.08)';
      if (props.color === 'secondary') return 'rgba(156, 39, 176, 0.08)';
      if (props.color === 'error') return 'rgba(211, 47, 47, 0.08)';
      if (props.color === 'warning') return 'rgba(237, 108, 2, 0.08)';
      if (props.color === 'info') return 'rgba(2, 136, 209, 0.08)';
      if (props.color === 'success') return 'rgba(46, 125, 50, 0.08)';
      return 'rgba(0, 0, 0, 0.04)';
    }};
  }
  
  svg {
    font-size: ${(props) => 
      props.size === 'small' 
        ? '16px' 
        : props.size === 'large' 
          ? '24px' 
          : '20px'
    };
  }
`;

// Компонент IconButton
const IconButton = forwardRef(({
  children,
  color = 'default',
  disabled = false,
  size = 'medium',
  onClick,
  // Анимации
  whileHover = { scale: 1.1 },
  whileTap = { scale: 0.9 },
  transition = { duration: 0.2 },
  ...props
}, ref) => {
  return (
    <IconButtonBase
      ref={ref}
      color={color}
      disabled={disabled}
      size={size}
      onClick={onClick}
      whileHover={disabled ? {} : whileHover}
      whileTap={disabled ? {} : whileTap}
      transition={transition}
      {...props}
    >
      {children}
    </IconButtonBase>
  );
});

// Компонент ButtonGroup
const ButtonGroupContainer = styled.div`
  display: inline-flex;
  border-radius: ${(props) => props.rounded ? '50px' : 'var(--border-radius, 4px)'};
  
  ${(props) => props.orientation === 'vertical' && css`
    flex-direction: column;
  `}
  
  ${(props) => props.fullWidth && css`
    width: 100%;
  `}
  
  ${(props) => props.variant === 'contained' && css`
    box-shadow: ${props.disableElevation 
      ? 'none' 
      : '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)'
    };
  `}
  
  & > button {
    ${(props) => props.orientation === 'horizontal' && css`
      &:not(:first-child) {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
      
      &:not(:last-child) {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        ${props.variant === 'outlined' && 'border-right-color: transparent;'}
      }
    `}
    
    ${(props) => props.orientation === 'vertical' && css`
      &:not(:first-child) {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
      
      &:not(:last-child) {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        ${props.variant === 'outlined' && 'border-bottom-color: transparent;'}
      }
    `}
    
    ${(props) => props.fullWidth && props.orientation === 'horizontal' && css`
      flex: 1;
    `}
  }
`;

const ButtonGroup = forwardRef(({
  children,
  variant = 'outlined',
  color = 'primary',
  size = 'medium',
  orientation = 'horizontal',
  fullWidth = false,
  rounded = false,
  disableElevation = false,
  ...props
}, ref) => {
  // Клонируем дочерние элементы с нужными свойствами
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        variant,
        color,
        size,
        disableElevation: variant === 'contained' ? disableElevation : undefined,
        // Сбрасываем hover/tap эффекты для корректной работы в группе
        whileHover: {},
        whileTap: { scale: 0.98 }
      });
    }
    return child;
  });

  return (
    <ButtonGroupContainer
      ref={ref}
      variant={variant}
      orientation={orientation}
      fullWidth={fullWidth}
      rounded={rounded}
      disableElevation={disableElevation}
      {...props}
    >
      {childrenWithProps}
    </ButtonGroupContainer>
  );
});

ButtonGroup.displayName = 'ButtonGroup';

// Экспортируем все компоненты и константы вместе
export { Button, IconButton, ButtonGroup };
export default Button; 